import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { useReturnToUrl } from "../../../../../core/router/navigation/returnTo";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { useContractVirtualSaveMutation } from "../../services/server/ContractApi";
import type { ContractEntity } from "../../types/ContractEntity";

export const ContractVirtualCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-virtual");
    const [searchParams] = useSearchParams();
    const siteId = searchParams.get("siteId") ?? undefined;

    const [formData, setFormData] = useState<ContractEntity>({ siteId } as ContractEntity);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const listPath = useReturnToUrl(`${BASE_PATH}/${ResourceName.CONTRACTS}`);
    const completeSubmit = useFormSubmitCompletion(formAction, listPath);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractVirtualSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", {
                        id: result.data.id,
                        resource: "Contract Virtual",
                    }),
                    `${BASE_PATH}/${ResourceName.CONTRACTS}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractEntity>
                    name="ContractVirtualCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
